import React, { useState, useCallback } from 'react';
import { Package, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const PRODUCTS = [
  'High Oleic Sunflower Oil',
  'High Oleic Sunflower Oil (Organic)',
  'Vegetable Distilled Fatty Acid',
  'Vegetable Acid Oil',
  'Hazelnut Fatty Acid',
  'Olive Squalene',
  'Refined Sunflower Oil',
  'Refined Canola Oil',
  'Extra Virgin Olive Oil',
  'Pomace Olive Oil',
  'Refined Soybean Oil',
  'Refined Corn Oil',
  'Crude Glycerin'
];

const PACKAGING_OPTIONS = [
  'Flexi Bags (Bulk - up to 24,000L)',
  'IBC Tanks (1,000L)',
  'PET Bottles (0.5L - 3L)',
  'PET Bottles (4.5L - 5L)',
  'Metal Tins (5L)',
  'Metal Tins (18L)'
];

const TRUST_INDICATORS = [
  { title: 'Fast Response', desc: 'Within 24 hours' },
  { title: 'Competitive Pricing', desc: 'Best market rates' },
  { title: 'Reliable Delivery', desc: 'Global shipping' },
];

const INITIAL_FORM = {
  product: '', packaging: '', quantity: '',
  destinationPort: '', additionalInfo: '',
  name: '', email: '', company: ''
};

const TrustIndicators = () => (
  <div className="grid md:grid-cols-3 gap-6 mt-12">
    {TRUST_INDICATORS.map((item) => (
      <div key={item.title} className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
        <p className="text-sm text-gray-600">{item.desc}</p>
      </div>
    ))}
  </div>
);

const QuickInquiry = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/send-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        toast({ title: "Inquiry Sent Successfully!", description: "Thank you for your inquiry. We'll get back to you soon." });
        setFormData(INITIAL_FORM);
      } else {
        throw new Error('Failed to send inquiry');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send inquiry. Please try again or contact us directly.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, toast, setFormData, setIsSubmitting]);

  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Package className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Quick Product Inquiry
            </h2>
            <p className="text-xl text-gray-600">
              Get a quote for your bulk order in minutes
            </p>
          </div>

          {/* Form Card */}
          <Card className="shadow-2xl border-2 border-blue-200">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <Input
                    value={formData.company}
                    onChange={(e) => updateField('company', e.target.value)}
                    placeholder="Your Company Ltd."
                  />
                </div>

                {/* Product Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Product *
                  </label>
                  <Select required value={formData.product} onValueChange={(value) => updateField('product', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCTS.map((product) => (
                        <SelectItem key={product} value={product}>
                          {product}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Packaging Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Packaging Type *
                    </label>
                    <Select required value={formData.packaging} onValueChange={(value) => updateField('packaging', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select packaging..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PACKAGING_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approximate Quantity *
                    </label>
                    <Input
                      required
                      value={formData.quantity}
                      onChange={(e) => updateField('quantity', e.target.value)}
                      placeholder="e.g., 20 tons / 1000 liters"
                    />
                  </div>
                </div>

                {/* Destination Port */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination Port *
                  </label>
                  <Input
                    required
                    value={formData.destinationPort}
                    onChange={(e) => updateField('destinationPort', e.target.value)}
                    placeholder="e.g., Port of Shanghai, China"
                  />
                </div>

                {/* Additional Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <Textarea
                    value={formData.additionalInfo}
                    onChange={(e) => updateField('additionalInfo', e.target.value)}
                    placeholder="Any specific requirements, delivery timeline, certifications needed, etc."
                    rows={4}
                    className="resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    <>Sending Inquiry...</>
                  ) : (
                    <>
                      Send Inquiry
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  Your inquiry will be sent directly to our sales team. We typically respond within 24 hours.
                </p>
              </form>
            </CardContent>
          </Card>

          <TrustIndicators />
        </div>
      </div>
    </section>
  );
};

export default QuickInquiry;
